import React, { useEffect, useState } from "react";
import { api } from "@/api";
import { useAuth } from "@/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, ExternalLink } from "lucide-react";

export default function CatalogoAdmin() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState({
    primary_color: "#0A0A0A", accent_color: "#002FA7", layout: "grid",
    dark_mode: false,
    show_phone: true, show_about: true, show_address: true,
    show_logo: true, show_name: true, show_whatsapp: true, show_instagram: true,
    enabled: true,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/catalog/config").then(r => setCfg(r.data)); }, []);

  const update = (k, v) => setCfg(p => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try { await api.put("/catalog/config", cfg); toast.success("Configuração salva"); }
    catch { toast.error("Erro ao salvar"); }
    finally { setBusy(false); }
  };

  const url = user?.impersonated_tenant?.slug
    ? `${window.location.origin}/catalogo/${user.impersonated_tenant.slug}`
    : user?.tenant?.slug
      ? `${window.location.origin}/catalogo/${user.tenant.slug}`
      : `${window.location.origin}/catalogo`;
  const copy = async () => { await navigator.clipboard.writeText(url); toast.success("Link copiado"); };

  return (
    <div className="space-y-8" data-testid="catalogo-admin-page">
      <div>
        <p className="label-tiny mb-2">Loja online</p>
        <h1 className="font-display text-4xl tracking-tight">Catálogo / Site</h1>
      </div>

      <Card className="p-6 border-[#E5E5E5] shadow-none bg-[#0A0A0A] text-white">
        <p className="label-tiny text-white/60">Link público</p>
        <div className="flex flex-wrap gap-2 mt-2 items-center">
          <code className="text-sm text-white/90 bg-white/10 px-3 py-2 rounded break-all flex-1">{url}</code>
          <Button variant="outline" onClick={copy} className="bg-white text-[#0A0A0A] border-white" data-testid="copy-catalog-link"><Copy size={14} className="mr-1"/> Copiar</Button>
          <a href={url} target="_blank" rel="noreferrer">
            <Button variant="outline" className="bg-white text-[#0A0A0A] border-white" data-testid="open-catalog-link"><ExternalLink size={14} className="mr-1"/> Abrir</Button>
          </a>
        </div>
      </Card>

      <Card className="p-8 border-[#E5E5E5] shadow-none">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="flex items-center justify-between">
            <div><Label>Catálogo ativo</Label><p className="text-xs text-neutral-500">Liga/desliga o site público</p></div>
            <Switch checked={cfg.enabled} onCheckedChange={(v)=>update("enabled", v)} data-testid="catalog-enabled-switch"/>
          </div>
          <div>
            <Label>Layout</Label>
            <Select value={cfg.layout} onValueChange={(v)=>update("layout", v)}>
              <SelectTrigger data-testid="catalog-layout-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="grid">Grade</SelectItem>
                <SelectItem value="list">Lista</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Cor primária</Label><Input type="color" value={cfg.primary_color} onChange={e=>update("primary_color", e.target.value)} data-testid="catalog-primary-color"/></div>
          <div><Label>Cor de destaque</Label><Input type="color" value={cfg.accent_color} onChange={e=>update("accent_color", e.target.value)} data-testid="catalog-accent-color"/></div>
          <div className="md:col-span-2 border-t border-[#E5E5E5] pt-4">
            <Label className="mb-2 block">Tema padrão do catálogo</Label>
            <div className="grid grid-cols-2 gap-2" data-testid="catalog-theme-toggle">
              <button
                type="button"
                onClick={() => update("dark_mode", false)}
                className={`flex items-center justify-center gap-2 py-3 px-4 rounded-md border transition ${!cfg.dark_mode ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] hover:border-neutral-400"}`}
                data-testid="catalog-theme-light">
                <span style={{fontSize: 16}}>☀</span> Claro
              </button>
              <button
                type="button"
                onClick={() => update("dark_mode", true)}
                className={`flex items-center justify-center gap-2 py-3 px-4 rounded-md border transition ${cfg.dark_mode ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] hover:border-neutral-400"}`}
                data-testid="catalog-theme-dark">
                <span style={{fontSize: 16}}>🌙</span> Escuro
              </button>
            </div>
            <p className="text-[11px] text-neutral-500 mt-2">Tema que o cliente vê ao abrir o catálogo. Ele pode trocar a qualquer momento no próprio site.</p>
          </div>
          <div className="flex items-center justify-between md:col-span-2 border-t border-[#E5E5E5] pt-4">
            <Label>Mostrar logo da loja</Label>
            <Switch checked={cfg.show_logo} onCheckedChange={(v)=>update("show_logo", v)} data-testid="show-logo-switch"/>
          </div>
          <div className="flex items-center justify-between md:col-span-2">
            <Label>Mostrar nome da loja</Label>
            <Switch checked={cfg.show_name} onCheckedChange={(v)=>update("show_name", v)} data-testid="show-name-switch"/>
          </div>
          <div className="flex items-center justify-between md:col-span-2">
            <Label>Mostrar telefone</Label>
            <Switch checked={cfg.show_phone} onCheckedChange={(v)=>update("show_phone", v)}/>
          </div>
          <div className="flex items-center justify-between md:col-span-2">
            <Label>Mostrar botão WhatsApp</Label>
            <Switch checked={cfg.show_whatsapp} onCheckedChange={(v)=>update("show_whatsapp", v)} data-testid="show-whatsapp-switch"/>
          </div>
          <div className="flex items-center justify-between md:col-span-2">
            <Label>Mostrar Instagram</Label>
            <Switch checked={cfg.show_instagram} onCheckedChange={(v)=>update("show_instagram", v)} data-testid="show-instagram-switch"/>
          </div>
          <div className="flex items-center justify-between md:col-span-2">
            <Label>Mostrar endereço</Label>
            <Switch checked={cfg.show_address} onCheckedChange={(v)=>update("show_address", v)}/>
          </div>
          <div className="flex items-center justify-between md:col-span-2">
            <Label>Mostrar "sobre"</Label>
            <Switch checked={cfg.show_about} onCheckedChange={(v)=>update("show_about", v)}/>
          </div>
        </div>
        <div className="mt-8 flex justify-end">
          <Button onClick={save} disabled={busy} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="save-catalog-config">{busy ? "Salvando…" : "Salvar"}</Button>
        </div>
      </Card>
    </div>
  );
}
